import React, { useState } from "react";

import {
  mdiArrowRight,
  mdiMovieOpenCogOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import SelectedMediaOwnerInfo from "./SelectedMediaOwnerInfo";
import { TIME_FORMAT_LONG } from "../../../constants/dateFormat";
import useFAST from "../../../contexts/FASTContext";
import useDeleteScheduleListTimeslotMutation from "../../../hooks/mutations/useDeleteScheduleListTimeslotMutation";
import useAbility from "../../../hooks/useAbility";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";
import SidepanelBlock from "../../block/SidepanelBlock";
import { BUTTON_VARIANTS } from "../../button/Button";
import Input from "../../form/Input";
import ConfirmModal from "../../modal/ConfirmModal";
import SidePanel from "../../sidePanel/SidePanel";
import FastScheduleTag from "../../tag/FastScheduleTag";

import type { JSX } from "react";

import type { AppState } from "../../../reducers";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { SchedulingTimeslotItem } from "../timeline/timeline.types";

interface FormValues {
  startTime?: string;
  endTime?: string;
  duration?: string | number;
  itemCount?: number;
}

interface SelectedTimeslotSidePanelProps {
  timelineTimeslotItems: SchedulingTimeslotItem[];
  isEditMode: boolean;
  isUpdateDisabled: boolean;
}

function SelectedTimeslotSidePanel({
  timelineTimeslotItems,
  isEditMode,
  isUpdateDisabled,
}: SelectedTimeslotSidePanelProps): JSX.Element | null {
  const history = useHistory();
  const { dispatch, state } = useFAST();
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const orgaOptions = useSelector(
    (globalState: AppState) =>
      globalState.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const { fastChannelsWrite: canAccessfastChannelsWrite } =
    canAccess(orgaOptions);
  const { getCanUpdateSchedule } = useFASTScheduleWebSocket();

  const deleteScheduleListTimeslotMutation =
    useDeleteScheduleListTimeslotMutation();

  const { timelineItemSelected } = state;

  const timeslotSelected = timelineTimeslotItems.find(
    (item) => item.id === timelineItemSelected,
  );

  const {
    scheduleId,
    start_time: startTime,
    end_time: endTime,
    itemIdList,
  } = timeslotSelected ?? {};

  const prettyStartTime =
    startTime && moment(startTime).format(TIME_FORMAT_LONG);
  const prettyEndTime = endTime && moment(endTime).format(TIME_FORMAT_LONG);

  const duration =
    startTime && endTime && moment(endTime).diff(moment(startTime));

  const fastDuration =
    duration &&
    prettyDurations(duration, { valueUnit: DURATION_UNITS.MILLISECONDS })
      .fastDuration;

  const methods = useForm<FormValues>({
    values: {
      startTime: prettyStartTime,
      endTime: prettyEndTime,
      duration: fastDuration,
      itemCount: itemIdList?.size,
    },
  });

  const handleClose = (): void =>
    dispatch({ type: "SET_ITEM_SELECTED", id: "" });

  if (!timeslotSelected) return null;

  const handleDeleteTimeslot = (): void => {
    handleClose();
    getCanUpdateSchedule({
      onSuccess: () =>
        deleteScheduleListTimeslotMutation.mutate({
          scheduleId: timeslotSelected.scheduleId,
          periodStart: timeslotSelected.start_time.format(),
          periodEnd: timeslotSelected.end_time.format(),
          itemIdList: timeslotSelected.itemIdList,
        }),
      onError: () => {
        toast.error(t("programDeleteError"));
      },
    });
  };

  const confirmModalActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModal(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("delete"),
      onClick: handleDeleteTimeslot,
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  const isInPast = moment(startTime).isBefore(moment());

  const getDisabledDeleteMessage = (): string => {
    if (!isEditMode) {
      return t("editModeNeeded");
    }

    if (isUpdateDisabled) {
      return t("canNotDeleteTimeslotDuringSync");
    }

    if (isInPast) {
      return t("canNotDeleteTimeslotInPast");
    }

    if (timeslotSelected.hasMediaVariant) {
      return t("canNotDeleteTimeslotWithVariant");
    }

    return t("canNotDeleteThisTimeslot");
  };

  const isDeleteDisabled =
    isInPast ||
    timeslotSelected.hasMediaVariant ||
    !isEditMode ||
    isUpdateDisabled;

  const sidePanelActions = canAccessfastChannelsWrite
    ? [
        {
          label: t("editTimeslot"),
          id: "edit-timeslot",
          isFullWidth: true,
          isFullHeight: true,
          onClick: (): void =>
            history.push(
              `/fast-channels/${channelId}/timeslots/${timeslotSelected.timeslot.uuid}`,
            ),
        },
        {
          label: t("deleteTimeslot"),
          id: "delete-timeslot",
          onClick: (): void => setShowModal(true),
          buttonVariant: BUTTON_VARIANTS.DELETE,
          isFullWidth: true,
          isFullHeight: true,
          isDisabled: isDeleteDisabled,
          tooltipOptions: isDeleteDisabled
            ? {
                id: "disabled-delete",
                content: getDisabledDeleteMessage(),
              }
            : undefined,
        },
      ]
    : undefined;

  const titleBlock = (
    <div className="flex w-full items-center gap-[0.9375rem] overflow-hidden">
      <Icon path={mdiMovieOpenCogOutline} size={0.9375} />
      <div className="flex text-[1.171875rem]">
        <div className="max-w-[325px] truncate">
          {timeslotSelected.timeslot.name}
        </div>
      </div>
      <SelectedMediaOwnerInfo
        updatedAt={timeslotSelected.updatedAt}
        userId={timeslotSelected.userId}
      />
    </div>
  );

  return (
    <SidePanel
      actions={sidePanelActions}
      isOpen={Boolean(timeslotSelected)}
      onClose={handleClose}
      title={titleBlock}
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        {scheduleId ? (
          <SidepanelBlock title={t("programInfo")}>
            <p className="font-medium">{t("associatedSchedule")}</p>
            <FastScheduleTag isFullWidth scheduleId={scheduleId} />
          </SidepanelBlock>
        ) : null}
        <Input
          isCompact
          isReadOnly
          label={t("scheduledPrograms")}
          name="itemCount"
        />
        <Input
          isCompact
          isReadOnly
          label={t("totalDuration")}
          name="duration"
        />
        <div className="flex items-end justify-between gap-[0.46875rem] [&>*]:mb-0">
          <Input isCompact isReadOnly label={t("startTime")} name="startTime" />
          <div className="border-primary bg-primary/10 flex size-[35px] shrink-0 items-center justify-center rounded-[0.46875rem] border p-[0.234375rem]">
            <Icon path={mdiArrowRight} size={0.9375} />
          </div>
          <Input isCompact isReadOnly label={t("endTime")} name="endTime" />
        </div>
        <ConfirmModal
          actions={confirmModalActions}
          iconPath={mdiTrashCanOutline}
          isOpen={showModal}
          onClose={(): void => setShowModal(false)}
          size="sm"
          title={t("deleteContentOfTimeslot")}
        >
          {t("deleteTimeslotSchedulingConfirmation")}
        </ConfirmModal>
      </FormProvider>
    </SidePanel>
  );
}

export default SelectedTimeslotSidePanel;
